/**
 * Comms History (v2.5.6)
 *
 * Landlord-visible log of every outbound message sent to tenants at a
 * property. Fed by the persistent MessageModel — closes the "no record
 * of what I sent" hole. Read-only.
 *
 * Rendered inside PropertyDetail. Expects the property's id.
 */
import React, { useEffect, useState } from 'react';
import { communicationService } from '../../../services/api';

interface Message {
    id: string;
    tenantId: string;
    propertyId?: string | null;
    channel: 'email' | 'sms';
    subject?: string;
    /** First ~500 chars, plaintext. Full body is NOT stored (data-minimisation). */
    bodySnippet?: string | null;
    attachmentUrl?: string | null;
    attachmentName?: string | null;
    sentBy?: { email?: string | null; sub?: string | null } | null;
    sentAt: string;
    deliveryStatus: 'sent' | 'failed' | 'bounced' | 'scheduled';
    providerRef?: string | null;
    provider?: string;
    error?: string | null;
    scheduledAt?: string | null;
    source?: string;
}

interface Props {
    propertyId: string;
    /** Optional: map tenantId → tenant name so we can label rows without an extra lookup. */
    tenantNames?: Record<string, string>;
}

const statusStyle = (s: Message['deliveryStatus']) => {
    switch (s) {
        case 'sent':      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'failed':    return 'bg-red-100 text-red-800 border-red-200';
        case 'bounced':   return 'bg-amber-100 text-amber-800 border-amber-200';
        default:          return 'bg-gray-100 text-gray-700 border-gray-200';
    }
};

const COLLAPSE_KEY = 'commsHistory.collapsed';

const CommsHistory: React.FC<Props> = ({ propertyId, tenantNames = {} }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    // Per-user collapse preference persists across page loads.
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    });
    const toggleCollapsed = () => {
        setCollapsed((c) => {
            const next = !c;
            try { window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch {}
            return next;
        });
    };

    useEffect(() => {
        if (!propertyId) return;
        let mounted = true;
        setLoading(true); setError(null);
        communicationService.listPropertyMessages(propertyId)
            .then((res: any) => {
                if (!mounted) return;
                const list = res.data?.data || [];
                setMessages(Array.isArray(list) ? list : []);
            })
            .catch((err: any) => {
                if (!mounted) return;
                setError(err?.response?.data?.message || err?.message || 'Failed to load comms history');
            })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [propertyId]);

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <button
                type="button"
                onClick={toggleCollapsed}
                aria-expanded={!collapsed}
                aria-controls="comms-history-body"
                className="w-full flex items-center justify-between text-left group"
            >
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <i className="fas fa-envelope-open-text text-indigo-500 mr-2"></i>
                    Comms History
                </h3>
                <span className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">{messages.length} message{messages.length === 1 ? '' : 's'}</span>
                    <i className={`fas fa-chevron-${collapsed ? 'down' : 'up'} text-gray-400 group-hover:text-gray-600`}></i>
                </span>
            </button>

            {collapsed ? null : (
            <div id="comms-history-body" className="mt-4">
            {loading ? (
                <div className="text-sm text-gray-400 italic">Loading…</div>
            ) : error ? (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>
            ) : messages.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-gray-100 rounded-xl">
                    <p className="text-sm text-gray-400">No messages sent yet.</p>
                    <p className="text-xs text-gray-400 mt-1">
                        Outbound messages sent via the Communications toolkit will appear here.
                    </p>
                </div>
            ) : (
                <ul className="divide-y divide-gray-100">
                    {messages.map((m) => {
                        const expanded = expandedId === m.id;
                        const recipient = tenantNames[m.tenantId] || m.tenantId;
                        return (
                            <li key={m.id} className="py-3">
                                <button
                                    type="button"
                                    onClick={() => setExpandedId(expanded ? null : m.id)}
                                    className="w-full text-left group"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-xs font-mono uppercase text-gray-500">{m.channel}</span>
                                                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${statusStyle(m.deliveryStatus)}`}>
                                                    {m.deliveryStatus}
                                                </span>
                                                {m.attachmentUrl && (
                                                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                                        <i className="fas fa-paperclip mr-1"></i>attachment
                                                    </span>
                                                )}
                                                {m.source === 'toolkit-backfill' && (
                                                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-800 border border-yellow-200" title="Reconstructed from audit log — body was not captured">
                                                        backfilled
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-700">
                                                {m.subject || '(no subject)'}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                To <strong>{recipient}</strong> · {new Date(m.sentAt).toLocaleString('en-GB')}
                                                {m.sentBy?.email ? <> · by {m.sentBy.email}</> : null}
                                            </p>
                                        </div>
                                        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} text-gray-400 mt-1`}></i>
                                    </div>
                                </button>
                                {expanded && (
                                    <div className="mt-3 ml-1 pl-3 border-l-2 border-indigo-100">
                                        {m.bodySnippet ? (
                                            <>
                                                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words p-2 bg-gray-50 rounded">
                                                    {m.bodySnippet}
                                                </p>
                                                <p className="text-[10px] text-gray-400 mt-1 italic">
                                                    Snippet only (first 500 chars). Full message is stored with the provider ({m.provider || 'email'}).
                                                </p>
                                            </>
                                        ) : (
                                            <p className="text-xs text-gray-400 italic">
                                                No snippet stored
                                                {m.source === 'toolkit-backfill' && ' (reconstructed from audit log — the original body lives with the provider)'}.
                                            </p>
                                        )}
                                        {m.attachmentUrl && (
                                            <p className="text-xs mt-2">
                                                <a href={m.attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                                                    <i className="fas fa-paperclip mr-1"></i>{m.attachmentName || 'Attachment'}
                                                </a>
                                            </p>
                                        )}
                                        {m.error && (
                                            <p className="text-xs mt-2 text-red-700 bg-red-50 border border-red-200 rounded p-2">
                                                <i className="fas fa-exclamation-triangle mr-1"></i>{m.error}
                                            </p>
                                        )}
                                        {m.providerRef && (
                                            <p className="text-[10px] text-gray-400 mt-2 font-mono">provider ref: {m.providerRef}</p>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            </div>
            )}
        </div>
    );
};

export default CommsHistory;
